'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useCallback } from 'react';
import { Track } from 'livekit-client';
import { useRemoteParticipants } from '@livekit/components-react';
import { ChatInput } from '@/components/livekit/chat/chat-input';
import { DeviceSelect } from '@/components/livekit/device-select';
import { TrackToggle } from '@/components/livekit/track-toggle';
import { useAgentControlBar } from '@/hooks/use-agent-control-bar';
import { cn } from '@/lib/utils';
/**
 * A control bar specifically designed for voice assistant interfaces
 */
export function ActionBar(_a) {
    var { controls, saveUserChoices = true, capabilities, className, onSendMessage, onChatOpenChange, onDeviceError } = _a, props = __rest(_a, ["controls", "saveUserChoices", "capabilities", "className", "onSendMessage", "onChatOpenChange", "onDeviceError"]);
    const participants = useRemoteParticipants();
    const [chatOpen] = React.useState(true);
    const [isSendingMessage, setIsSendingMessage] = React.useState(false);
    const isAgentAvailable = participants.some((p) => p.isAgent);
    const isInputDisabled = !chatOpen || !isAgentAvailable || isSendingMessage;
    const { visibleControls, cameraToggle, screenShareToggle, handleVideoDeviceChange } = useAgentControlBar({
        controls,
        saveUserChoices,
    });
    const handleSendMessage = async (message) => {
        setIsSendingMessage(true);
        try {
            await (onSendMessage === null || onSendMessage === void 0 ? void 0 : onSendMessage(message));
        }
        finally {
            setIsSendingMessage(false);
        }
    };
    React.useEffect(() => {
        onChatOpenChange === null || onChatOpenChange === void 0 ? void 0 : onChatOpenChange(chatOpen);
    }, [chatOpen, onChatOpenChange]);
    const onCameraDeviceSelectError = useCallback((error) => {
        onDeviceError === null || onDeviceError === void 0 ? void 0 : onDeviceError({ source: Track.Source.Camera, error });
    }, [onDeviceError]);
    return (_jsxs("div", Object.assign({ "aria-label": "Voice assistant controls", className: cn('bg-white px-4 py-2', className), style: { borderTop: '1px solid #E5E7EB' } }, props, { children: [capabilities.supportsChatInput && (_jsx("div", { inert: !chatOpen, className: cn('overflow-hidden transition-[height] duration-300 ease-out', chatOpen ? 'h-auto' : 'h-0'), children: _jsx(ChatInput, { onSend: handleSendMessage, disabled: isInputDisabled, className: "w-full" }) })), (capabilities.supportsVideoInput || capabilities.supportsScreenShare) && (_jsxs("div", { className: "mt-2 flex flex-row justify-between gap-1", children: [_jsx("div", { className: "flex gap-1", children: capabilities.supportsVideoInput && visibleControls.camera && (_jsxs("div", { className: "flex items-center gap-0", children: [_jsx(TrackToggle, { variant: "primary", source: Track.Source.Camera, pressed: cameraToggle.enabled, pending: cameraToggle.pending, disabled: cameraToggle.pending, onPressedChange: cameraToggle.toggle, className: "peer/track relative w-auto pr-3 pl-3 disabled:opacity-100 has-[+_*]:rounded-r-none has-[+_*]:border-r-0 has-[+_*]:pr-2" }), _jsx(DeviceSelect, { size: "sm", kind: "videoinput", requestPermissions: false, onMediaDeviceError: onCameraDeviceSelectError, onActiveDeviceChange: handleVideoDeviceChange, className: cn([
                                        'pl-2',
                                        'peer-data-[state=off]/track:text-destructive-foreground',
                                        'hover:text-fg1 focus:text-fg1',
                                        'hover:peer-data-[state=off]/track:text-destructive-foreground focus:peer-data-[state=off]/track:text-destructive-foreground',
                                        'rounded-l-none',
                                    ]) })] })) }), _jsx("div", { className: "flex gap-1", children: capabilities.supportsScreenShare && visibleControls.screenShare && (_jsx("div", { className: "flex items-center gap-0", children: _jsx(TrackToggle, { variant: "secondary", source: Track.Source.ScreenShare, pressed: screenShareToggle.enabled, disabled: screenShareToggle.pending, onPressedChange: screenShareToggle.toggle, className: "relative w-auto" }) })) })] }))] })));
}
